/** VBO/VAO/FBO helpers shared by the simulation and the renderer. */

export function must<T>(value: T | null, what: string): T {
  if (value === null) throw new Error(`WebGL allocation failed: ${what}`);
  return value;
}

/** Mirrors the particle layout in sim/state.ts: 2 x vec4, 32 bytes. */
export const PARTICLE_STRIDE_BYTES = 32;

export function createStateBuffer(
  gl: WebGL2RenderingContext,
  init: Float32Array | number,
): WebGLBuffer {
  const buffer = must(gl.createBuffer(), "state buffer");
  gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
  if (typeof init === "number") {
    gl.bufferData(gl.ARRAY_BUFFER, init, gl.DYNAMIC_COPY); // byte length
  } else {
    gl.bufferData(gl.ARRAY_BUFFER, init, gl.DYNAMIC_COPY); // initial contents
  }
  gl.bindBuffer(gl.ARRAY_BUFFER, null);
  return buffer;
}

/**
 * VAO for one particle state buffer: location 0 = a_posAgeLife (vec4),
 * location 1 = a_rand (vec4). Programs bind matching attrib locations.
 */
export function createParticleVao(
  gl: WebGL2RenderingContext,
  buffer: WebGLBuffer,
): WebGLVertexArrayObject {
  const vao = must(gl.createVertexArray(), "particle VAO");
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
  gl.enableVertexAttribArray(0);
  gl.vertexAttribPointer(0, 4, gl.FLOAT, false, PARTICLE_STRIDE_BYTES, 0);
  gl.enableVertexAttribArray(1);
  gl.vertexAttribPointer(1, 4, gl.FLOAT, false, PARTICLE_STRIDE_BYTES, 16);
  gl.bindVertexArray(null);
  gl.bindBuffer(gl.ARRAY_BUFFER, null);
  return vao;
}

/** GPU-side copy — used by the snapshot ring and export state restore. */
export function copyBuffer(
  gl: WebGL2RenderingContext,
  src: WebGLBuffer,
  dst: WebGLBuffer,
  byteLength: number,
): void {
  gl.bindBuffer(gl.COPY_READ_BUFFER, src);
  gl.bindBuffer(gl.COPY_WRITE_BUFFER, dst);
  gl.copyBufferSubData(gl.COPY_READ_BUFFER, gl.COPY_WRITE_BUFFER, 0, 0, byteLength);
  gl.bindBuffer(gl.COPY_READ_BUFFER, null);
  gl.bindBuffer(gl.COPY_WRITE_BUFFER, null);
}

export function createRenderTexture(
  gl: WebGL2RenderingContext,
  width: number,
  height: number,
  float16: boolean,
): WebGLTexture {
  const tex = must(gl.createTexture(), "render texture");
  gl.bindTexture(gl.TEXTURE_2D, tex);
  if (float16) {
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA16F, width, height, 0, gl.RGBA, gl.HALF_FLOAT, null);
  } else {
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA8, width, height, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
  }
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  gl.bindTexture(gl.TEXTURE_2D, null);
  return tex;
}

/** FBO with a single color attachment. Returns null if incomplete. */
export function createFramebuffer(
  gl: WebGL2RenderingContext,
  tex: WebGLTexture,
): WebGLFramebuffer | null {
  const fbo = must(gl.createFramebuffer(), "framebuffer");
  gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
  gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, tex, 0);
  const status = gl.checkFramebufferStatus(gl.FRAMEBUFFER);
  gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  if (status !== gl.FRAMEBUFFER_COMPLETE) {
    gl.deleteFramebuffer(fbo);
    return null;
  }
  return fbo;
}
